import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { HpCardHeader, HpTag, Icon, TableScroll } from '@hp/ui';
import type { MergeTask } from './queries';
import { StatusBadge } from '../../components/badges';
import { RelativeTime } from '../../components/relative-time';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { prettyJson, truncate } from '../../lib/format';

export function TaskTable({ tasks }: { tasks: MergeTask[] }) {
  const { t } = useTranslation(['jobs', 'common']);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  function toggleExpanded(taskId: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  return (
    <section className="hp-card">
      <HpCardHeader title={t('tasks.title')} subtitle={t('tasks.subtitle')} icon="list" />
      <TableScroll label={t('tasks.label')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('tasks.seq')}</th>
            <th>{t('tasks.cluster')}</th>
            <th>{t('tasks.survivor')}</th>
            <th>{t('tasks.losers')}</th>
            <th>{t('common:table.status')}</th>
            <th>{t('tasks.attempts')}</th>
            <th>{t('common:table.executed')}</th>
            <th>{t('tasks.error')}</th>
          </BrutTheadRow>
          <tbody>
            {tasks.map((task) => {
              const expandable = task.patchPlan !== null && task.patchPlan !== undefined;
              const isOpen = expanded.has(task.id);
              const rowClass = [expandable ? 'bo-row--link' : '', isOpen ? 'bo-row--expanded' : ''].join(' ').trim();
              return (
                <Fragment key={task.id}>
                  <tr
                    className={rowClass || undefined}
                    onClick={expandable ? () => toggleExpanded(task.id) : undefined}
                    title={expandable ? t('tasks.rowTitle') : undefined}
                    aria-expanded={expandable ? isOpen : undefined}
                  >
                    <td className="bo-cell-muted">
                      {expandable && (
                        <span className={isOpen ? 'bo-expand-toggle is-open' : 'bo-expand-toggle'} aria-hidden="true">
                          <Icon name="chevronDown" />
                        </span>
                      )}
                      {task.seq}
                    </td>
                    <td>
                      <Link
                        to={`/crm-data/deduplication/${task.clusterId}`}
                        className="hp-detail-action hp-detail-action--text"
                        onClick={(e) => e.stopPropagation()}
                      >
                        #{task.clusterId}
                      </Link>
                    </td>
                    <td>
                      {task.action === 'delete' ? (
                        <HpTag tone="danger" title={t('tasks.deleteTargets')}>
                          {t('tasks.actionDelete')}
                        </HpTag>
                      ) : (
                        <>#{task.survivorPdId}</>
                      )}
                    </td>
                    <td className="bo-cell-muted">
                      <span className="bo-chips">
                        {task.loserPdIds.map((pdId) => {
                          const alreadyMerged = task.mergedLoserIds.includes(pdId);
                          const doneTitle =
                            task.action === 'delete' ? t('tasks.alreadyDeleted') : t('tasks.alreadyMerged');
                          return (
                            <span
                              key={pdId}
                              className={alreadyMerged ? 'bo-chip bo-chip--done' : 'bo-chip'}
                              title={alreadyMerged ? doneTitle : undefined}
                            >
                              #{pdId}
                            </span>
                          );
                        })}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={task.status} />
                    </td>
                    <td>{task.attempts}</td>
                    <td>
                      <RelativeTime value={task.executedAt} />
                    </td>
                    <td className="bo-cell-error">
                      {task.lastError && (
                        <span className="bo-clip" title={task.lastError}>
                          {truncate(task.lastError, 80)}
                        </span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bo-row--expanded">
                      <td colSpan={8} className="bo-cell-detail">
                        <span className="bo-strong">{t('tasks.patchPlan')}</span>
                        <pre className="bo-code">{prettyJson(task.patchPlan)}</pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {tasks.length === 0 && (
              <tr>
                <td colSpan={8} className="bo-cell-empty">
                  {t('tasks.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </BrutTable>
      </TableScroll>
    </section>
  );
}
